import React, { useEffect, useMemo, useState } from 'react';
import type { TranslationSaveItem } from '../../services/i18nService';
import {
  countRows,
  missingPlaceholders,
  ROW_STATE_LABELS,
  type RowState,
  type TranslationRow,
} from '../../utils/translationRows';
import { useDialog } from '../../context/DialogContext';
import styles from '../../styles/translationAdmin.module.css';

export type ReviewFilter = 'attention' | RowState | 'all';

const FILTERS: { id: ReviewFilter; label: string }[] = [
  { id: 'attention', label: 'Needs attention' },
  { id: 'missing', label: ROW_STATE_LABELS.missing },
  { id: 'stale', label: ROW_STATE_LABELS.stale },
  { id: 'ai_draft', label: ROW_STATE_LABELS.ai_draft },
  { id: 'draft', label: ROW_STATE_LABELS.draft },
  { id: 'approved', label: ROW_STATE_LABELS.approved },
  { id: 'all', label: 'All' },
];

const PAGE_SIZE = 50;

interface SectionReviewProps {
  languageName: string;
  title: string;
  description?: string;
  rows: TranslationRow[];
  initialFilter?: ReviewFilter;
  initialQuery?: string;
  onBack: () => void;
  onSave: (items: TranslationSaveItem[]) => Promise<void>;
}

function matchesFilter(row: TranslationRow, filter: ReviewFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'attention') return row.state !== 'approved';
  return row.state === filter;
}

function matchesQuery(row: TranslationRow, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    row.key.toLowerCase().includes(needle) ||
    row.english.toLowerCase().includes(needle) ||
    row.text.toLowerCase().includes(needle)
  );
}

function formatWhen(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const SectionReview: React.FC<SectionReviewProps> = ({
  languageName,
  title,
  description,
  rows,
  initialFilter = 'attention',
  initialQuery = '',
  onBack,
  onSave,
}) => {
  const [filter, setFilter] = useState<ReviewFilter>(initialFilter);
  const [query, setQuery] = useState(initialQuery);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { confirmAsync } = useDialog();

  useEffect(() => {
    setVisible(PAGE_SIZE);
    setSelected(new Set());
  }, [filter, query]);

  const counts = useMemo(() => countRows(rows), [rows]);
  const filtered = useMemo(
    () => rows.filter((r) => matchesFilter(r, filter) && matchesQuery(r, query.trim())),
    [rows, filter, query]
  );
  const shown = filtered.slice(0, visible);

  const filterCount = (id: ReviewFilter): number => {
    if (id === 'all') return counts.total;
    if (id === 'attention') return counts.total - counts.approved;
    return counts[id];
  };

  const currentText = (row: TranslationRow) => edits[row.key] ?? row.text;
  const isDirty = (row: TranslationRow) => edits[row.key] !== undefined && edits[row.key] !== row.text;

  const clearEdit = (key: string) =>
    setEdits((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const runSave = async (items: TranslationSaveItem[], keys: string[], bulk = false) => {
    setError(null);
    if (bulk) setBulkSaving(true);
    else
      setSavingKeys((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.add(k));
        return next;
      });
    try {
      await onSave(items);
      keys.forEach(clearEdit);
      setSelected((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (bulk) setBulkSaving(false);
      else
        setSavingKeys((prev) => {
          const next = new Set(prev);
          keys.forEach((k) => next.delete(k));
          return next;
        });
    }
  };

  const saveRow = (row: TranslationRow, approve: boolean) => {
    const text = currentText(row);
    const status = approve ? 'approved' : isDirty(row) ? 'draft' : row.serverStatus ?? 'draft';
    void runSave([{ key: row.key, text, status, source_hash: row.englishHash }], [row.key]);
  };

  const approvable = (row: TranslationRow) => currentText(row).trim() !== '' && (row.state !== 'approved' || isDirty(row));

  const bulkApprove = async (targetRows: TranslationRow[]) => {
    const ready = targetRows.filter(approvable);
    if (ready.length === 0) return;
    const warnCount = ready.filter((r) => missingPlaceholders(r.english, currentText(r)).length > 0).length;
    const ok = await confirmAsync({
      title: `Approve ${ready.length} translation${ready.length === 1 ? '' : 's'}?`,
      message: warnCount
        ? `${warnCount} of them ${warnCount === 1 ? 'is' : 'are'} missing a {placeholder} from the English. Approve anyway?`
        : 'Only approve strings you have read.',
      confirmText: 'Approve',
      cancelText: 'Cancel',
      variant: warnCount ? 'warning' : 'info',
    });
    if (!ok) return;
    void runSave(
      ready.map((r) => ({ key: r.key, text: currentText(r), status: 'approved', source_hash: r.englishHash })),
      ready.map((r) => r.key),
      true
    );
  };

  const toggleSelected = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const approvableShown = shown.filter(approvable);
  const selectedRows = filtered.filter((r) => selected.has(r.key));

  return (
    <div>
      <div className={styles.reviewHeader}>
        <button type="button" className={styles.linkButton} onClick={onBack}>
          ← Back to {languageName} overview
        </button>
      </div>

      <div className={styles.card}>
        <h2>{title}</h2>
        {description && <p className={styles.muted}>{description}</p>}

        <div className={styles.filterTabs}>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={filter === f.id ? styles.filterTabActive : styles.filterTab}
              onClick={() => setFilter(f.id)}
            >
              {f.label} ({filterCount(f.id)})
            </button>
          ))}
        </div>

        <div className={styles.searchRow}>
          <input
            className={styles.input}
            type="search"
            placeholder="Search English, translation, or key"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {filtered.length > 0 && (
        <div className={styles.bulkBar}>
          <span>
            Showing {shown.length} of {filtered.length}
            {selectedRows.length > 0 && ` · ${selectedRows.length} selected`}
          </span>
          <button
            type="button"
            className={styles.buttonSmall}
            disabled={bulkSaving || selectedRows.filter(approvable).length === 0}
            onClick={() => void bulkApprove(selectedRows)}
          >
            Approve selected
          </button>
          <button
            type="button"
            className={styles.buttonSmallSecondary}
            disabled={bulkSaving || approvableShown.length === 0}
            onClick={() => void bulkApprove(approvableShown)}
          >
            Approve all shown ({approvableShown.length})
          </button>
          {bulkSaving && <span className={styles.muted}>Saving…</span>}
        </div>
      )}

      {filtered.length === 0 && (
        <div className={styles.card}>
          <p className={styles.muted}>Nothing here — try another filter.</p>
        </div>
      )}

      {shown.map((row) => {
        const text = currentText(row);
        const dirty = isDirty(row);
        const saving = savingKeys.has(row.key);
        const missing = missingPlaceholders(row.english, text);
        const rows = Math.min(10, Math.max(2, Math.ceil(Math.max(row.english.length, text.length) / 60)));
        return (
          <div key={row.key} className={styles.stringRow}>
            <div className={styles.stringRowHeader}>
              <input
                type="checkbox"
                aria-label={`Select ${row.key}`}
                checked={selected.has(row.key)}
                onChange={() => toggleSelected(row.key)}
              />
              <span className={styles.stringKey}>{row.key}</span>
              <span className={styles[`badge_${row.state}`]}>{ROW_STATE_LABELS[row.state]}</span>
            </div>
            <div className={styles.stringColumns}>
              <div>
                <div className={styles.columnLabel}>English</div>
                <div className={styles.englishText}>{row.english}</div>
              </div>
              <div>
                <div className={styles.columnLabel}>{languageName}</div>
                <textarea
                  className={dirty ? styles.textareaDirty : styles.textarea}
                  rows={rows}
                  value={text}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [row.key]: e.target.value }))}
                />
                {text.trim() !== '' && missing.length > 0 && (
                  <div className={styles.warning}>
                    Missing {missing.join(', ')} — keep placeholders exactly as in the English.
                  </div>
                )}
                {row.state === 'stale' && (
                  <div className={styles.warning}>
                    The English changed since this was translated. Update it if needed, then approve.
                  </div>
                )}
              </div>
            </div>
            <div className={styles.rowActions}>
              <button
                type="button"
                className={styles.buttonSmall}
                disabled={saving || !approvable(row)}
                onClick={() => saveRow(row, true)}
              >
                {dirty ? 'Save & approve' : 'Approve'}
              </button>
              <button
                type="button"
                className={styles.buttonSmallSecondary}
                disabled={saving || !dirty}
                onClick={() => saveRow(row, false)}
              >
                Save draft
              </button>
              {dirty && (
                <button type="button" className={styles.linkButton} onClick={() => clearEdit(row.key)}>
                  Undo changes
                </button>
              )}
              {saving && <span className={styles.muted}>Saving…</span>}
            </div>
            {(row.approvedAt || row.updatedAt) && (
              <div className={styles.rowMeta}>
                {row.state === 'approved' && row.approvedBy
                  ? `Approved by ${row.approvedBy} · ${formatWhen(row.approvedAt)}`
                  : row.updatedBy
                    ? `Last saved by ${row.updatedBy} · ${formatWhen(row.updatedAt)}`
                    : ''}
              </div>
            )}
          </div>
        );
      })}

      {filtered.length > visible && (
        <div className={styles.actions}>
          <button type="button" className={styles.buttonSecondary} onClick={() => setVisible((v) => v + PAGE_SIZE)}>
            Show more ({filtered.length - visible} remaining)
          </button>
        </div>
      )}
    </div>
  );
};

export default SectionReview;
